import { useEffect } from 'react';
import { setConfig } from '@leagueplatform/core';
import type { PermissionsHookProps } from '@web-config-app/core';
import { QueryStatus } from '@tanstack/react-query';
import { useConfigApp } from '../use-config-app/use-config-app';
import { useHasPermissionsHelpers } from '../use-has-permissions-helpers/use-has-permissions-helpers';
import { filterEntitiesByPermissions } from './filter-entities-by-permissions.util';

interface UseFilterEntitiesBasedOnPermissionsProps
  extends PermissionsHookProps {
  permissionsRequestStatus: QueryStatus;
}
export const useFilterEntitiesBasedOnPermissions = ({
  isAuthenticated,
  permissionsRequestStatus,
  environmentKey,
  tenantId,
}: UseFilterEntitiesBasedOnPermissionsProps) => {
  const { entityConfigs } = useConfigApp();
  const { hasAllPermissions } = useHasPermissionsHelpers({
    environmentKey,
    isAuthenticated,
    tenantId,
  });

  useEffect(() => {
    if (isAuthenticated && permissionsRequestStatus === 'success') {
      const filteredEntityConfigs = filterEntitiesByPermissions(
        entityConfigs,
        hasAllPermissions,
      );
      setConfig((draft) => {
        const updatedConfigApp = {
          ...(draft.configApp ?? {}),
          entityConfigs: filteredEntityConfigs,
          permissionsFilterComplete: true,
        };
        // eslint-disable-next-line no-param-reassign
        draft.configApp = updatedConfigApp;
      });
    }
    // `entityConfigs` is intentionally excluded — including it would cause an
    // infinite loop since this effect writes back to configApp via setConfig.
    // `hasAllPermissions` is also excluded: `usePermissions` returns a fresh
    // empty array while the query is pending, which would give
    // `hasAllPermissions` a new identity each render and re-trigger the loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, permissionsRequestStatus]);
};
